import { createContext, useContext, useMemo, useRef, useState } from 'react'
import type { Dispatch, ReactNode, RefObject, SetStateAction } from 'react'

export type ChatDraft = { draft: string; error: string; alsoRequest: boolean }
export type RequestDraft = { open: boolean; title: string; error: string; isSending: boolean }

type TeamDrafts = {
  chat: ChatDraft
  setChat: Dispatch<SetStateAction<ChatDraft>>
  request: RequestDraft
  setRequest: Dispatch<SetStateAction<RequestDraft>>
  requestSending: RefObject<boolean>
}

export const EMPTY_CHAT_DRAFT: ChatDraft = {
  draft: '',
  error: '',
  alsoRequest: true,
}
export const EMPTY_REQUEST_DRAFT: RequestDraft = {
  open: false,
  title: '',
  error: '',
  isSending: false,
}

const TeamDraftsContext = createContext<TeamDrafts | null>(null)

// Drafts live above the tabs and the panel, so tab switches, collapse and the Charts hide keep them.
export const TeamDraftsProvider = ({ children }: { children: ReactNode }) => {
  const [chat, setChat] = useState(EMPTY_CHAT_DRAFT)
  const [request, setRequest] = useState(EMPTY_REQUEST_DRAFT)
  const requestSending = useRef(false)
  const value = useMemo(
    () => ({ chat, setChat, request, setRequest, requestSending }),
    [chat, request],
  )
  return <TeamDraftsContext.Provider value={value}>{children}</TeamDraftsContext.Provider>
}

export const useTeamDrafts = (): TeamDrafts => {
  const drafts = useContext(TeamDraftsContext)
  if (!drafts) throw new Error('useTeamDrafts must be used inside TeamDraftsProvider')
  return drafts
}
